import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "../observability/report-client-error.js";

interface ErrorBoundaryProps {
  /** Names the area in the report and the fallback copy. */
  area: string;
  children: ReactNode;
  /** Remounts the children when this key changes (e.g. the route). */
  resetKey?: string;
}

interface ErrorBoundaryState {
  failed: boolean;
}

/**
 * Keeps one broken page from unmounting the whole app. The scorecard gets
 * its own boundary so an offline round survives a render bug elsewhere.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public state: ErrorBoundaryState = { failed: false };

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError(error, {
      area: this.props.area,
      componentStack: info.componentStack ?? null,
    });
  }

  public componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  public render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <section className="fr-panel fr-error-fallback" role="alert">
        <p className="fr-eyebrow">Something went wrong</p>
        <h2 className="fr-h2">This part of the app failed to load.</h2>
        <p className="fr-lead">
          Your saved rounds are safe on this device. Reload to try again.
        </p>
        <button
          type="button"
          className="fr-btn"
          data-variant="gold"
          data-size="block"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </section>
    );
  }
}
